import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { fadeIn } from '../../utils/animations';
import { useAuth } from '../../providers/AuthProvider';
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../config/firebase';

const LangarCalendarContainer = styled.div`
  width: 100%;
  height: 85vh;
  padding: 1.5rem;
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.text};
  display: flex;
  flex-direction: column;
  opacity: 0;
  animation: ${fadeIn} 0.5s ease-out forwards;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1rem 0;
  border-bottom: 2px solid ${(props) => props.theme.colors.borders};

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
`;

const CalendarTitle = styled.h2`
  font-family: ${(props) => props.theme.fontFamilies.primary};
  font-size: ${(props) => props.theme.fontSizes.h2};
  font-weight: 700;
  color: ${(props) => props.theme.colors.primary};
  margin: 0;
`;

const MonthNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const NavButton = styled.button`
  padding: 0.5rem 1rem;
  border: 2px solid ${(props) => props.theme.colors.primary};
  border-radius: ${(props) => props.theme.borderRadius};
  background-color: transparent;
  color: ${(props) => props.theme.colors.primary};
  font-family: ${(props) => props.theme.fontFamilies.primary};
  font-size: ${(props) => props.theme.fontSizes.body};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${(props) => props.theme.colors.primary};
    color: ${(props) => props.theme.colors.surface};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CurrentMonth = styled.span`
  font-family: ${(props) => props.theme.fontFamilies.primary};
  font-size: ${(props) => props.theme.fontSizes.h3};
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  min-width: 200px;
  text-align: center;
`;

const SundayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  flex: 1;
  overflow-y: auto;
  padding: 1rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const SundayCard = styled.div<{ $isBooked: boolean; $isPast: boolean }>`
  background-color: ${(props) =>
    props.$isBooked
      ? props.theme.colors.secondary
      : props.$isPast
      ? props.theme.colors.background
      : props.theme.colors.surface};
  border: 2px solid
    ${(props) =>
      props.$isBooked
        ? props.theme.colors.secondary
        : props.$isPast
        ? props.theme.colors.borders
        : props.theme.colors.primary};
  border-radius: ${(props) => props.theme.borderRadius};
  padding: 1.5rem;
  cursor: ${(props) =>
    props.$isPast || props.$isBooked ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  opacity: ${(props) => (props.$isPast ? 0.5 : 1)};
  position: relative;
  overflow: hidden;
  min-height: ${(props) => (props.$isBooked ? '280px' : '180px')};
  display: flex;
  flex-direction: column;

  &:hover {
    transform: ${(props) =>
      props.$isPast || props.$isBooked ? 'none' : 'translateY(-2px)'};
    box-shadow: ${(props) =>
      props.$isPast || props.$isBooked
        ? 'none'
        : '0 8px 25px rgba(0, 0, 0, 0.15)'};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background-color: ${(props) =>
      props.$isBooked
        ? props.theme.colors.secondary
        : props.$isPast
        ? props.theme.colors.borders
        : props.theme.colors.primary};
  }
`;

const SundayDateDisplay = styled.div`
  font-family: ${(props) => props.theme.fontFamilies.primary};
  font-size: ${(props) => props.theme.fontSizes.h3};
  font-weight: 700;
  color: ${(props) => props.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const SundayDay = styled.div`
  font-family: ${(props) => props.theme.fontFamilies.secondary};
  font-size: ${(props) => props.theme.fontSizes.body};
  color: ${(props) => props.theme.colors.textSecondary};
  margin-bottom: 1rem;
`;

const BookingStatus = styled.div<{ $isBooked: boolean }>`
  font-family: ${(props) => props.theme.fontFamilies.secondary};
  font-size: ${(props) => props.theme.fontSizes.body};
  font-weight: 600;
  color: ${(props) =>
    props.$isBooked ? props.theme.colors.surface : props.theme.colors.text};
  text-align: center;
  padding: 0.5rem;
  border-radius: ${(props) => props.theme.borderRadius};
  background-color: ${(props) =>
    props.$isBooked ? props.theme.colors.secondary : 'transparent'};
`;

const BookedBy = styled.div`
  font-family: ${(props) => props.theme.fontFamilies.secondary};
  font-size: ${(props) => props.theme.fontSizes.small};
  color: ${(props) => props.theme.colors.textSecondary};
  margin-top: 0.5rem;
  text-align: center;
`;

const BookingDetails = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${(props) => props.theme.colors.borders};
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  font-family: ${(props) => props.theme.fontFamilies.secondary};
  font-size: ${(props) => props.theme.fontSizes.small};
  font-weight: 600;
  color: ${(props) => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailValue = styled.span`
  font-family: ${(props) => props.theme.fontFamilies.secondary};
  font-size: ${(props) => props.theme.fontSizes.small};
  color: ${(props) => props.theme.colors.text};
  text-align: right;
  max-width: 60%;
  word-wrap: break-word;
`;

const DescriptionText = styled.div`
  font-family: ${(props) => props.theme.fontFamilies.secondary};
  font-size: ${(props) => props.theme.fontSizes.small};
  color: ${(props) => props.theme.colors.text};
  margin-top: 0.5rem;
  padding: 0.5rem;
  background-color: ${(props) => props.theme.colors.background};
  border-radius: ${(props) => props.theme.borderRadius};
  border-left: 3px solid ${(props) => props.theme.colors.primary};
`;

const CreatedDate = styled.div`
  font-family: ${(props) => props.theme.fontFamilies.secondary};
  font-size: ${(props) => props.theme.fontSizes.small};
  color: ${(props) => props.theme.colors.textSecondary};
  margin-top: 0.5rem;
  text-align: center;
  font-style: italic;
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background-color: ${(props) => props.theme.colors.error || '#ff4444'};
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  z-index: 10;

  &:hover {
    background-color: ${(props) => props.theme.colors.error || '#cc0000'};
    transform: scale(1.1);
  }
`;

const AuthModeToggle = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const AuthModeButton = styled.button<{ $isActive: boolean }>`
  flex: 1;
  padding: 0.5rem;
  border: 2px solid
    ${(props) =>
      props.$isActive
        ? props.theme.colors.primary
        : props.theme.colors.borders};
  border-radius: ${(props) => props.theme.borderRadius};
  background-color: ${(props) =>
    props.$isActive ? props.theme.colors.primary : 'transparent'};
  color: ${(props) =>
    props.$isActive ? props.theme.colors.surface : props.theme.colors.text};
  font-family: ${(props) => props.theme.fontFamilies.primary};
  font-size: ${(props) => props.theme.fontSizes.body};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${(props) => props.theme.colors.primary};
    background-color: ${(props) =>
      props.$isActive
        ? props.theme.colors.primary
        : props.theme.colors.primary}20;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${(props) => props.theme.colors.textSecondary};
  font-family: ${(props) => props.theme.fontFamilies.secondary};
  font-size: ${(props) => props.theme.fontSizes.body};
`;

// Phone Authentication Modal
const AuthModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  padding: 1rem;

  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const AuthModalContent = styled.div<{ $isOpen: boolean }>`
  background-color: ${(props) => props.theme.colors.surface};
  border-radius: ${(props) => props.theme.borderRadius};
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  transform: ${(props) =>
    props.$isOpen ? 'translateY(0)' : 'translateY(100%)'};
  transition: transform 0.3s ease-out;

  @media (max-width: 768px) {
    max-width: 100%;
    max-height: 90vh;
    border-radius: ${(props) => props.theme.borderRadius}
      ${(props) => props.theme.borderRadius} 0 0;
  }
`;

const AuthHeader = styled.div`
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid ${(props) => props.theme.colors.borders};
  text-align: center;

  @media (max-width: 768px) {
    padding: 1.5rem 1.5rem 1rem 1.5rem;
  }
`;

const AuthTitle = styled.h2`
  font-family: ${(props) => props.theme.fontFamilies.primary};
  font-size: ${(props) => props.theme.fontSizes.h2};
  font-weight: 700;
  color: ${(props) => props.theme.colors.primary};
  margin: 0;
  line-height: 1.2;
`;

const AuthSubtitle = styled.p`
  font-family: ${(props) => props.theme.fontFamilies.secondary};
  font-size: ${(props) => props.theme.fontSizes.body};
  color: ${(props) => props.theme.colors.textSecondary};
  margin: 0.5rem 0 0 0;
`;

const AuthBody = styled.div`
  padding: 1.5rem 2rem 2rem 2rem;

  @media (max-width: 768px) {
    padding: 1rem 1.5rem 1.5rem 1.5rem;
  }
`;

const AuthField = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const AuthLabel = styled.label`
  display: block;
  font-family: ${(props) => props.theme.fontFamilies.primary};
  font-size: ${(props) => props.theme.fontSizes.body};
  font-weight: 600;
  color: ${(props) => props.theme.colors.textSecondary};
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AuthInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid ${(props) => props.theme.colors.borders};
  border-radius: ${(props) => props.theme.borderRadius};
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.text};
  font-family: ${(props) => props.theme.fontFamilies.secondary};
  font-size: ${(props) => props.theme.fontSizes.body};
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: 0 0 0 2px ${(props) => props.theme.colors.primary}40;
  }
`;

const AuthButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: ${(props) => props.theme.borderRadius};
  font-family: ${(props) => props.theme.fontFamilies.primary};
  font-size: ${(props) => props.theme.fontSizes.body};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: ${(props) =>
    props.$variant === 'secondary'
      ? props.theme.colors.secondary
      : props.theme.colors.primary};
  color: ${(props) => props.theme.colors.surface};
  width: 100%;
  margin-bottom: 1rem;

  &:hover {
    background-color: ${(props) =>
      props.$variant === 'secondary'
        ? props.theme.colors.textSecondary
        : props.theme.colors.accentHover};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  background-color: ${(props) => props.theme.colors.error || '#ff4444'};
  color: white;
  padding: 0.75rem 1rem;
  border-radius: ${(props) => props.theme.borderRadius};
  margin-bottom: 1rem;
  font-family: ${(props) => props.theme.fontFamilies.secondary};
  font-size: ${(props) => props.theme.fontSizes.body};
`;

const SuccessMessage = styled.div`
  background-color: ${(props) => props.theme.colors.success || '#44ff44'};
  color: white;
  padding: 0.75rem 1rem;
  border-radius: ${(props) => props.theme.borderRadius};
  margin-bottom: 1rem;
  font-family: ${(props) => props.theme.fontFamilies.secondary};
  font-size: ${(props) => props.theme.fontSizes.body};
`;

// Langar Booking Modal
const BookingModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  padding: 1rem;

  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const BookingModalContent = styled.div<{ $isOpen: boolean }>`
  background-color: ${(props) => props.theme.colors.surface};
  border-radius: ${(props) => props.theme.borderRadius};
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  transform: ${(props) =>
    props.$isOpen ? 'translateY(0)' : 'translateY(100%)'};
  transition: transform 0.3s ease-out;

  @media (max-width: 768px) {
    max-width: 100%;
    max-height: 90vh;
    border-radius: ${(props) => props.theme.borderRadius}
      ${(props) => props.theme.borderRadius} 0 0;
  }
`;

const BookingHeader = styled.div`
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid ${(props) => props.theme.colors.borders};

  @media (max-width: 768px) {
    padding: 1.5rem 1.5rem 1rem 1.5rem;
  }
`;

const BookingTitle = styled.h2`
  font-family: ${(props) => props.theme.fontFamilies.primary};
  font-size: ${(props) => props.theme.fontSizes.h2};
  font-weight: 700;
  color: ${(props) => props.theme.colors.primary};
  margin: 0;
  line-height: 1.2;
`;

const BookingBody = styled.div`
  padding: 1.5rem 2rem 2rem 2rem;

  @media (max-width: 768px) {
    padding: 1rem 1.5rem 1.5rem 1.5rem;
  }
`;

const BookingField = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const BookingLabel = styled.label`
  display: block;
  font-family: ${(props) => props.theme.fontFamilies.primary};
  font-size: ${(props) => props.theme.fontSizes.body};
  font-weight: 600;
  color: ${(props) => props.theme.colors.textSecondary};
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const BookingInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid ${(props) => props.theme.colors.borders};
  border-radius: ${(props) => props.theme.borderRadius};
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.text};
  font-family: ${(props) => props.theme.fontFamilies.secondary};
  font-size: ${(props) => props.theme.fontSizes.body};
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: 0 0 0 2px ${(props) => props.theme.colors.primary}40;
  }
`;

const BookingTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid ${(props) => props.theme.colors.borders};
  border-radius: ${(props) => props.theme.borderRadius};
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.text};
  font-family: ${(props) => props.theme.fontFamilies.secondary};
  font-size: ${(props) => props.theme.fontSizes.body};
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: 0 0 0 2px ${(props) => props.theme.colors.primary}40;
  }
`;

const BookingActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const BookingButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: ${(props) => props.theme.borderRadius};
  font-family: ${(props) => props.theme.fontFamilies.primary};
  font-size: ${(props) => props.theme.fontSizes.body};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: ${(props) =>
    props.$variant === 'secondary'
      ? props.theme.colors.secondary
      : props.theme.colors.primary};
  color: ${(props) => props.theme.colors.surface};

  &:hover {
    background-color: ${(props) =>
      props.$variant === 'secondary'
        ? props.theme.colors.textSecondary
        : props.theme.colors.accentHover};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const LoginPromptOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const LoginPromptContent = styled.div`
  background-color: ${(props) => props.theme.colors.surface};
  border-radius: ${(props) => props.theme.borderRadius};
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 100%;
  padding: 2rem;
  text-align: center;
  border: 2px solid ${(props) => props.theme.colors.primary};
`;

const LoginPromptTitle = styled.h3`
  font-family: ${(props) => props.theme.fontFamilies.primary};
  font-size: ${(props) => props.theme.fontSizes.h3};
  color: ${(props) => props.theme.colors.primary};
  margin: 0 0 1rem 0;
`;

const LoginPromptText = styled.p`
  font-family: ${(props) => props.theme.fontFamilies.secondary};
  font-size: ${(props) => props.theme.fontSizes.body};
  color: ${(props) => props.theme.colors.textSecondary};
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
`;

const LoginButton = styled.button`
  padding: 0.75rem 2rem;
  border: none;
  border-radius: ${(props) => props.theme.borderRadius};
  font-family: ${(props) => props.theme.fontFamilies.primary};
  font-size: ${(props) => props.theme.fontSizes.body};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: ${(props) => props.theme.colors.primary};
  color: ${(props) => props.theme.colors.surface};

  &:hover {
    background-color: ${(props) => props.theme.colors.accentHover};
    transform: translateY(-1px);
  }
`;

// Interfaces
interface LangarBooking {
  id: string;
  name: string;
  phone: string;
  date: string;
  description?: string;
  createdAt: Timestamp;
  userId: string;
}

interface BookingFormData {
  name: string;
  phone: string;
  description: string;
}

interface SundayDate {
  date: Date;
  dateString: string;
  isBooked: boolean;
  isPast: boolean;
  booking?: LangarBooking;
}

const LangarCalendarComponent: React.FC = () => {
  const {
    user,
    isAdmin,
    sendPhoneVerification,
    verifyPhoneCode,
    login,
    register,
    logout,
  } = useAuth();
  const [bookings, setBookings] = useState<LangarBooking[]>([]);
  const [sundayDates, setSundayDates] = useState<SundayDate[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState<any>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [authMode, setAuthMode] = useState<'phone' | 'email'>('phone');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [bookingFormData, setBookingFormData] = useState<BookingFormData>({
    name: '',
    phone: '',
    description: '',
  });

  const fetchLangarBookings = async () => {
    try {
      const q = query(collection(db, 'langarBookings'), orderBy('date', 'asc'));
      const querySnapshot = await getDocs(q);
      const bookingList: LangarBooking[] = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as LangarBooking[];
      setBookings(bookingList);
    } catch (error) {
      console.error('Error fetching langar bookings:', error);
      setError('Failed to load langar bookings');
    }
  };

  // Generate Sundays for the current month
  const generateSundays = useCallback(
    (month: Date) => {
      const year = month.getFullYear();
      const monthIndex = month.getMonth();

      // Get first day of month and first Sunday
      const firstDay = new Date(year, monthIndex, 1);
      const firstSunday = new Date(firstDay);
      firstSunday.setDate(firstDay.getDate() + ((7 - firstDay.getDay()) % 7));

      const sundays: SundayDate[] = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Generate all Sundays in the month
      for (
        let date = new Date(firstSunday);
        date.getMonth() === monthIndex;
        date.setDate(date.getDate() + 7)
      ) {
        const dateString = date.toISOString().split('T')[0];
        const isPast = date < today;
        const booking = bookings.find((b) => b.date === dateString);

        sundays.push({
          date: new Date(date),
          dateString,
          isBooked: !!booking,
          isPast,
          booking,
        });
      }

      setSundayDates(sundays);
    },
    [bookings]
  );

  // Navigation functions
  const goToPreviousMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() - 1);
    setCurrentMonth(newMonth);
  };

  const goToNextMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + 1);
    setCurrentMonth(newMonth);
  };

  const goToCurrentMonth = () => {
    setCurrentMonth(new Date());
  };

  useEffect(() => {
    fetchLangarBookings();
  }, []);

  useEffect(() => {
    // Regenerate Sundays when bookings or current month changes
    generateSundays(currentMonth);
  }, [bookings, currentMonth, generateSundays]);

  useEffect(() => {
    // Refetch bookings when user authentication state changes
    console.log('🔄 User auth state changed:', !!user);
    fetchLangarBookings();
  }, [user]);

  const handleSundayClick = (sundayDate: SundayDate) => {
    console.log('🗓️ Sunday clicked:', sundayDate.dateString);
    console.log('👤 User authenticated:', !!user);
    console.log('📅 Is booked:', sundayDate.isBooked);
    console.log('📅 Is past:', sundayDate.isPast);

    // Check if date is in the past
    if (sundayDate.isPast) {
      console.log('❌ Past date, blocking booking');
      setError('Cannot book langar for past dates');
      setTimeout(() => {
        setError('');
      }, 4000);
      return;
    }

    // Check if already booked
    if (sundayDate.isBooked) {
      console.log('❌ Already booked, blocking booking');
      setError('This Sunday is already booked for langar');
      setTimeout(() => {
        setError('');
      }, 4000);
      return;
    }

    if (!user) {
      console.log('❌ User not authenticated, opening auth modal');
      setError(''); // Clear any previous error messages
      setSuccess(''); // Clear any previous success messages
      setIsAuthModalOpen(true);
      return;
    }

    console.log('✅ All checks passed, opening booking modal');
    setError(''); // Clear any previous error messages
    setSuccess(''); // Clear any previous success messages
    setSelectedDate(sundayDate.dateString);
    setIsBookingModalOpen(true);
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsVerifying(true);

    try {
      const result = await sendPhoneVerification(phoneNumber);
      setConfirmationResult(result);
      setSuccess('Verification code sent to your phone');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error: any) {
      setError(error.message || 'Failed to send verification code');
      // Clear error message after 5 seconds
      setTimeout(() => {
        setError('');
      }, 5000);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsVerifying(true);

    try {
      await verifyPhoneCode(confirmationResult, verificationCode);
      setSuccess('Phone verified successfully!');
      setIsAuthModalOpen(false);
      setPhoneNumber('');
      setVerificationCode('');
      setConfirmationResult(null);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error: any) {
      setError(error.message || 'Invalid verification code');
      // Clear error message after 5 seconds
      setTimeout(() => {
        setError('');
      }, 5000);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsVerifying(true);

    try {
      await login(email, password);
      setSuccess('Login successful!');
      setIsAuthModalOpen(false);
      setEmail('');
      setPassword('');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error: any) {
      setError(error.message || 'Login failed');
      // Clear error message after 5 seconds
      setTimeout(() => {
        setError('');
      }, 5000);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleEmailRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsRegistering(true);

    try {
      await register(email, password);
      setSuccess('Registration successful!');
      setIsAuthModalOpen(false);
      setEmail('');
      setPassword('');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error: any) {
      setError(error.message || 'Registration failed');
      // Clear error message after 5 seconds
      setTimeout(() => {
        setError('');
      }, 5000);
    } finally {
      setIsRegistering(false);
    }
  };

  const handleDeleteBooking = async (bookingId: string) => {
    if (!isAdmin) return;

    const confirmed = window.confirm(
      'Are you sure you want to delete this booking?'
    );
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, 'langarBookings', bookingId));
      setSuccess('Booking deleted successfully!');
      await fetchLangarBookings();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error: any) {
      setError(error.message || 'Failed to delete booking');
      // Clear error message after 5 seconds
      setTimeout(() => {
        setError('');
      }, 5000);
    }
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsBooking(true);

    try {
      const bookingData = {
        name: bookingFormData.name,
        phone: bookingFormData.phone,
        date: selectedDate,
        description: bookingFormData.description,
        createdAt: serverTimestamp(),
        userId: user?.uid || '',
      };

      await addDoc(collection(db, 'langarBookings'), bookingData);
      setSuccess('Langar booking confirmed successfully!');
      setIsBookingModalOpen(false);
      setBookingFormData({ name: '', phone: '', description: '' });
      await fetchLangarBookings();

      // Clear success message after 4 seconds
      setTimeout(() => {
        setSuccess('');
      }, 4000);
    } catch (error: any) {
      setError(error.message || 'Failed to book langar');
      // Clear error message after 5 seconds
      setTimeout(() => {
        setError('');
      }, 5000);
    } finally {
      setIsBooking(false);
    }
  };

  const closeAuthModal = () => {
    setIsAuthModalOpen(false);
    setPhoneNumber('');
    setVerificationCode('');
    setConfirmationResult(null);
    setEmail('');
    setPassword('');
    setAuthMode('phone');
    setIsRegistering(false);
    setError('');
    setSuccess('');
  };

  const closeBookingModal = () => {
    setIsBookingModalOpen(false);
    setSelectedDate('');
    setBookingFormData({ name: '', phone: '', description: '' });
    setError('');
    setSuccess('');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCreatedDate = (timestamp: Timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      <LangarCalendarContainer>
        {/* Logout Button */}

        {/* Error and Success Messages */}
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        {/* Calendar Header */}
        <CalendarHeader>
          <CalendarTitle>Langar Booking Calendar</CalendarTitle>
          <MonthNavigation>
            <NavButton onClick={goToPreviousMonth}>← Previous</NavButton>
            <CurrentMonth>
              {currentMonth.toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric',
              })}
            </CurrentMonth>
            <NavButton onClick={goToNextMonth}>Next →</NavButton>
            <NavButton onClick={goToCurrentMonth}>Today</NavButton>
            {user && <NavButton onClick={() => logout()}>Logout</NavButton>}
          </MonthNavigation>
        </CalendarHeader>

        {/* Sunday Grid */}
        <SundayGrid>
          {sundayDates.length === 0 ? (
            <EmptyState>No Sundays available for this month</EmptyState>
          ) : (
            sundayDates.map((sunday) => (
              <SundayCard
                key={sunday.dateString}
                $isBooked={sunday.isBooked}
                $isPast={sunday.isPast}
                onClick={() => handleSundayClick(sunday)}
              >
                <SundayDateDisplay>{sunday.date.getDate()}</SundayDateDisplay>
                <SundayDay>
                  {sunday.date.toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </SundayDay>
                <BookingStatus $isBooked={sunday.isBooked}>
                  {sunday.isPast
                    ? 'Past Date'
                    : sunday.isBooked
                    ? 'Booked'
                    : 'Available'}
                </BookingStatus>
                {sunday.isBooked && sunday.booking && (
                  <>
                    {isAdmin && (
                      <DeleteButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteBooking(sunday.booking!.id);
                        }}
                        title="Delete booking"
                      >
                        ×
                      </DeleteButton>
                    )}
                    <BookedBy>Booked by: {sunday.booking.name}</BookedBy>
                    <BookingDetails>
                      <DetailRow>
                        <DetailLabel>Name:</DetailLabel>
                        <DetailValue>{sunday.booking.name}</DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Phone:</DetailLabel>
                        <DetailValue>{sunday.booking.phone}</DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Date:</DetailLabel>
                        <DetailValue>
                          {formatDate(sunday.booking.date)}
                        </DetailValue>
                      </DetailRow>
                      {sunday.booking.description && (
                        <div>
                          <DetailLabel>Notes:</DetailLabel>
                          <DescriptionText>
                            {sunday.booking.description}
                          </DescriptionText>
                        </div>
                      )}
                      <CreatedDate>
                        Booked on: {formatCreatedDate(sunday.booking.createdAt)}
                      </CreatedDate>
                    </BookingDetails>
                  </>
                )}
              </SundayCard>
            ))
          )}
        </SundayGrid>

        {!user && (
          <LoginPromptOverlay>
            <LoginPromptContent>
              <LoginPromptTitle>Login Required</LoginPromptTitle>
              <LoginPromptText>
                Please login with your phone number or email to book langar
                slots
              </LoginPromptText>
              <LoginButton
                onClick={() => {
                  setError(''); // Clear any previous error messages
                  setSuccess(''); // Clear any previous success messages
                  setIsAuthModalOpen(true);
                }}
              >
                Login
              </LoginButton>
            </LoginPromptContent>
          </LoginPromptOverlay>
        )}
      </LangarCalendarContainer>

      {/* reCAPTCHA container */}
      <div id="recaptcha-container"></div>

      {/* Phone Authentication Modal */}
      <AuthModalOverlay
        $isOpen={isAuthModalOpen}
        onClick={(e) => e.target === e.currentTarget && closeAuthModal()}
      >
        <AuthModalContent $isOpen={isAuthModalOpen}>
          <AuthHeader>
            <AuthTitle>Login / Register</AuthTitle>
            <AuthSubtitle>Choose your preferred login method</AuthSubtitle>
          </AuthHeader>

          <AuthBody>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}

            {/* Auth Mode Toggle */}
            <AuthModeToggle>
              <AuthModeButton
                $isActive={authMode === 'phone'}
                onClick={() => setAuthMode('phone')}
              >
                Phone
              </AuthModeButton>
              <AuthModeButton
                $isActive={authMode === 'email'}
                onClick={() => setAuthMode('email')}
              >
                Email
              </AuthModeButton>
            </AuthModeToggle>

            {authMode === 'phone' ? (
              !confirmationResult ? (
                <form onSubmit={handlePhoneSubmit}>
                  <AuthField>
                    <AuthLabel>Phone Number</AuthLabel>
                    <AuthInput
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="+1234567890 (include country code)"
                      required
                    />
                  </AuthField>
                  <AuthButton type="submit" disabled={isVerifying}>
                    {isVerifying ? 'Sending...' : 'Send Verification Code'}
                  </AuthButton>
                </form>
              ) : (
                <form onSubmit={handleCodeSubmit}>
                  <AuthField>
                    <AuthLabel>Verification Code</AuthLabel>
                    <AuthInput
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      placeholder="Enter 6-digit code"
                      required
                    />
                  </AuthField>
                  <AuthButton type="submit" disabled={isVerifying}>
                    {isVerifying ? 'Verifying...' : 'Verify Code'}
                  </AuthButton>
                  <AuthButton
                    type="button"
                    $variant="secondary"
                    onClick={() => setConfirmationResult(null)}
                  >
                    Back to Phone Number
                  </AuthButton>
                </form>
              )
            ) : (
              <div>
                <form
                  onSubmit={
                    isRegistering ? handleEmailRegister : handleEmailLogin
                  }
                >
                  <AuthField>
                    <AuthLabel>Email</AuthLabel>
                    <AuthInput
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                    />
                  </AuthField>
                  <AuthField>
                    <AuthLabel>Password</AuthLabel>
                    <AuthInput
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                    />
                  </AuthField>
                  <AuthButton
                    type="submit"
                    disabled={isVerifying || isRegistering}
                  >
                    {isVerifying || isRegistering
                      ? isRegistering
                        ? 'Registering...'
                        : 'Logging in...'
                      : isRegistering
                      ? 'Register'
                      : 'Login'}
                  </AuthButton>
                </form>
                <AuthButton
                  type="button"
                  $variant="secondary"
                  onClick={() => setIsRegistering(!isRegistering)}
                >
                  {isRegistering
                    ? 'Already have an account? Login'
                    : 'Need an account? Register'}
                </AuthButton>
              </div>
            )}
          </AuthBody>
        </AuthModalContent>
      </AuthModalOverlay>

      {/* Langar Booking Modal */}
      <BookingModalOverlay
        $isOpen={isBookingModalOpen}
        onClick={(e) => e.target === e.currentTarget && closeBookingModal()}
      >
        <BookingModalContent $isOpen={isBookingModalOpen}>
          <BookingHeader>
            <BookingTitle>Book Langar</BookingTitle>
          </BookingHeader>

          <BookingBody>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}

            <BookingField>
              <BookingLabel>Selected Date</BookingLabel>
              <BookingInput
                type="text"
                value={formatDate(selectedDate)}
                disabled
              />
            </BookingField>

            <form onSubmit={handleBookingSubmit}>
              <BookingField>
                <BookingLabel>Your Name</BookingLabel>
                <BookingInput
                  type="text"
                  value={bookingFormData.name}
                  onChange={(e) =>
                    setBookingFormData({
                      ...bookingFormData,
                      name: e.target.value,
                    })
                  }
                  placeholder="Enter your full name"
                  required
                />
              </BookingField>

              <BookingField>
                <BookingLabel>Phone Number</BookingLabel>
                <BookingInput
                  type="tel"
                  value={bookingFormData.phone}
                  onChange={(e) =>
                    setBookingFormData({
                      ...bookingFormData,
                      phone: e.target.value,
                    })
                  }
                  placeholder="+1234567890"
                  required
                />
              </BookingField>

              <BookingField>
                <BookingLabel>Additional Notes (Optional)</BookingLabel>
                <BookingTextarea
                  value={bookingFormData.description}
                  onChange={(e) =>
                    setBookingFormData({
                      ...bookingFormData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Any special requirements or notes..."
                />
              </BookingField>

              <BookingActions>
                <BookingButton
                  type="button"
                  $variant="secondary"
                  onClick={closeBookingModal}
                >
                  Cancel
                </BookingButton>
                <BookingButton type="submit" disabled={isBooking}>
                  {isBooking ? 'Booking...' : 'Confirm Booking'}
                </BookingButton>
              </BookingActions>
            </form>
          </BookingBody>
        </BookingModalContent>
      </BookingModalOverlay>
    </>
  );
};

export default React.memo(LangarCalendarComponent);
